import type { DefaultPaymentLogsSessionListPatch, ListParamsForSession } from '../types/utils'
/**
 * Mirrors Angular `AuthService` session usage for `defaultpaymentlogs_filter`
 * (see `ReportsDefaultPaymentLogsComponent` / company `defaultPayment`).
 */

import { DEFAULT_PAYMENT_LOGS_SESSION_KEY } from '../constants/sessionKeys'

export { DEFAULT_PAYMENT_LOGS_SESSION_KEY }

export function readDefaultPaymentLogsSession(): Record<string, unknown> | null {
    try {
        const raw = sessionStorage.getItem(DEFAULT_PAYMENT_LOGS_SESSION_KEY)
        if (!raw) return null
        const v = JSON.parse(raw) as unknown
        return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null
    } catch {
        return null
    }
}

export function writeDefaultPaymentLogsSession(data: Record<string, unknown>): void {
    sessionStorage.setItem(DEFAULT_PAYMENT_LOGS_SESSION_KEY, JSON.stringify(data))
}

export function clearDefaultPaymentLogsSession(): void {
    sessionStorage.removeItem(DEFAULT_PAYMENT_LOGS_SESSION_KEY)
}

/** Normalize session/API dates (DD-MM-YYYY or YYYY-MM-DD) to YYYY-MM-DD for React date inputs. */
export function normalizeStoredDate(value: unknown): string {
    if (value == null) return ''
    const s = String(value).trim()
    if (!s) return ''
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
    const dmy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(s)
    if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`
    return ''
}

function strId(value: unknown): string {
    if (value == null || value === '') return ''
    return String(value)
}

function pickPaymentStatus(session: Record<string, unknown>): string[] {
    const v = session['status'] ?? session['status[]']
    if (Array.isArray(v)) return v.map(String).filter(Boolean)
    if (typeof v === 'string' && v.trim()) return [v.trim()]
    return []
}

function pickVerified(session: Record<string, unknown>): '' | '0' | '1' {
    const v = session['payment_verified']
    if (v === '0' || v === '1' || v === 0 || v === 1) return String(v) as '0' | '1'
    return ''
}

function pickReissue(session: Record<string, unknown>): '' | '0' | '1' {
    const v = session['is_reissue_payment']
    if (v === '0' || v === '1' || v === 0 || v === 1) return String(v) as '0' | '1'
    return ''
}

/**
 * Maps a session blob (Angular or React-written) into list params + optional property label
 * for the property combobox (Angular stores `property_name` alongside `property_id`).
 */
export function sessionFilterToListParamsPatch(session: Record<string, unknown>): {
    patch: DefaultPaymentLogsSessionListPatch
    propertyNameHint: string
} {
    const patch: DefaultPaymentLogsSessionListPatch = {}

    if (typeof session['page'] === 'number' && session['page'] > 0) patch.page = session['page']
    if (typeof session['perPage'] === 'number' && session['perPage'] > 0) patch.perPage = session['perPage']
    if (session['sortBy'] === 'asc' || session['sortBy'] === 'desc') patch.sortBy = session['sortBy']
    if (typeof session['orderBy'] === 'string' && session['orderBy'].trim()) patch.orderBy = session['orderBy']

    if (typeof session['q'] === 'string') patch.q = session['q']

    const pid = strId(session['property_id'])
    if (pid) patch.property_id = pid

    const cid = strId(session['company_id'])
    if (cid) patch.company_id = cid

    const fromDt = normalizeStoredDate(session['fromDt'])
    const toDt = normalizeStoredDate(session['toDt'])
    if (fromDt) patch.fromDt = fromDt
    if (toDt) patch.toDt = toDt

    const pv = pickVerified(session)
    if (pv) patch.payment_verified = pv

    const statuses = pickPaymentStatus(session)
    if (statuses.length) patch.payment_status = statuses

    if (typeof session['claim_status'] === 'string') patch.claim_status = session['claim_status']
    if (typeof session['vintage_year'] === 'string') patch.vintage_year = session['vintage_year']

    const lsFrom = normalizeStoredDate(session['lease_start_fromDt'])
    const lsTo = normalizeStoredDate(session['lease_start_toDt'])
    if (lsFrom) patch.lease_start_fromDt = lsFrom
    if (lsTo) patch.lease_start_toDt = lsTo

    const csFrom = normalizeStoredDate(session['claim_status_fromDt'])
    const csTo = normalizeStoredDate(session['claim_status_toDt'])
    if (csFrom) patch.claim_status_fromDt = csFrom
    if (csTo) patch.claim_status_toDt = csTo

    const re = pickReissue(session)
    if (re) patch.is_reissue_payment = re

    const propertyNameHint =
        typeof session['property_name'] === 'string' ? session['property_name'] : ''

    return { patch, propertyNameHint }
}

/** React date inputs (YYYY-MM-DD) → DD-MM-YYYY for session parity with Angular `urlParams`. */
function ymdToDmy(ymd: string): string {
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ''
    const [y, m, d] = ymd.split('-')
    return `${d}-${m}-${y}`
}

/**
 * Persists filters the same way Angular `saveFilter()` does (session JSON + property/company names).
 */
export function buildSessionFilterFromListParams(
    p: ListParamsForSession,
    opts: { propertyDisplayName?: string },
): Record<string, unknown> {
    const out: Record<string, unknown> = {
        page: p.page,
        perPage: p.perPage,
        sortBy: p.sortBy,
        orderBy: p.orderBy,
        q: p.q,
    }
    if (p.property_id) {
        out.property_id = p.property_id
        if (opts.propertyDisplayName) out.property_name = opts.propertyDisplayName
    }
    if (p.company_id) out.company_id = p.company_id

    const fd = ymdToDmy(p.fromDt)
    const td = ymdToDmy(p.toDt)
    if (fd) out.fromDt = fd
    if (td) out.toDt = td

    if (p.payment_verified === '0' || p.payment_verified === '1') {
        out.payment_verified = p.payment_verified
    }
    if (p.payment_status.length) out.status = [...p.payment_status]
    if (p.claim_status.trim()) out.claim_status = p.claim_status.trim()
    if (p.vintage_year.trim()) out.vintage_year = p.vintage_year.trim()

    const lsf = ymdToDmy(p.lease_start_fromDt)
    const lst = ymdToDmy(p.lease_start_toDt)
    if (lsf) out.lease_start_fromDt = lsf
    if (lst) out.lease_start_toDt = lst

    const csf = ymdToDmy(p.claim_status_fromDt)
    const cst = ymdToDmy(p.claim_status_toDt)
    if (csf) out.claim_status_fromDt = csf
    if (cst) out.claim_status_toDt = cst

    if (p.is_reissue_payment === '0' || p.is_reissue_payment === '1') {
        out.is_reissue_payment = p.is_reissue_payment
    }

    return out
}
